import React, { useEffect, useRef, useState } from 'react';
import './MaskedVideoFrame.css';
import OptimizedImage from './OptimizedImage';

const MaskedVideoFrame = ({
  topText,
  mainText,
  videoSrc,
  imageSrc,
  imageWebp,
  imageAvif,
  maskId,
  className = '',
  videoContainerClassName = '',
  mobileTallLayout = false,
  isActive = true,
  children,
}) => {
  const videoRef = useRef(null);
  const frameRef = useRef(null);
  const topTextRef = useRef(null);
  const mainTextRef = useRef(null);

  // When a line is wider than the frame, clamp it to the available width with
  // SVG textLength so it shrinks to fit instead of overflowing the viewport.
  const [textLengths, setTextLengths] = useState({ top: null, main: null });

  const useMobileLayout = mobileTallLayout;

  // Reference design dimensions
  const refW = useMobileLayout ? 390 : 1200;
  const refH = useMobileLayout ? 680 : 750;

  // Track rendered pixel size so mask coordinates match CSS pixel space exactly.
  // SVG mask-image coordinates are always in CSS pixels of the masked element,
  // not in viewBox units — so the viewBox must match the element's pixel size.
  const [dims, setDims] = useState({ w: refW, h: refH });

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) {
        setDims({ w: Math.round(width), h: Math.round(height) });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || imageSrc) return;
    if (isActive) {
      video.currentTime = 0;
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isActive, videoSrc]);

  const aspectRatio = useMobileLayout ? '390/680' : '16/10';

  // Scale all coordinates from reference space to actual rendered pixel space
  const sx = dims.w / refW;
  const sy = dims.h / refH;

  // Available horizontal space for the title — keep it within the rounded rect
  // inset so long lines never bleed past the frame edges.
  const maxTextWidth = Math.round((useMobileLayout ? 358 : 1120) * sx);

  // Measure each line after layout; only clamp the ones that actually overflow.
  useEffect(() => {
    const measure = (ref) => {
      const len = ref.current?.getComputedTextLength?.() ?? 0;
      return len > maxTextWidth ? maxTextWidth : null;
    };
    const remeasure = () =>
      setTextLengths({ top: measure(topTextRef), main: measure(mainTextRef) });
    remeasure();
    // The condensed title font is narrower than the fallback; re-measure once
    // it loads so we don't clamp based on the wider fallback metrics.
    document.fonts?.ready.then(remeasure);
  }, [dims, topText, mainText, useMobileLayout, maxTextWidth]);

  const textX = dims.w / 2;
  const topTextY   = Math.round((useMobileLayout ? 45  : 100) * sy);
  const mainTextY  = Math.round((useMobileLayout ? 90  : 190) * sy);
  const fontSize   = Math.round((useMobileLayout ? 38  : 100) * sy);
  const rectX      = Math.round((useMobileLayout ? 16  :  40) * sx);
  const rectY      = Math.round((useMobileLayout ? 89  : 189) * sy);
  const rectW      = Math.round((useMobileLayout ? 358 : 1120) * sx);
  const rectH      = Math.round((useMobileLayout ? 546 :  560) * sy);
  const rectRx     = Math.round((useMobileLayout ? 24  :  32) * sx);

  return (
    <div
      ref={frameRef}
      className={`masked-video-frame ${className}`.trim()}
      style={{ aspectRatio }}
    >
      <svg
        viewBox={`0 0 ${dims.w} ${dims.h}`}
        preserveAspectRatio='xMidYMid meet'
        aria-hidden='true'
        className='masked-video-frame__svg'
        width='100%'
        height='100%'
      >
        <defs>
          <mask id={maskId}>
            <text
              x={textX}
              y={topTextY}
              textAnchor='middle'
              fill='white'
              fontSize={fontSize}
              fontWeight='700'
              fontFamily="'Swiss721BT Bold Condensed', sans-serif"
              letterSpacing='-0.05em'
              textLength={textLengths.top || undefined}
              lengthAdjust={textLengths.top ? 'spacingAndGlyphs' : undefined}
              style={{ textTransform: 'uppercase' }}
            >
              {topText}
            </text>
            <text
              x={textX}
              y={mainTextY}
              textAnchor='middle'
              fill='white'
              fontSize={fontSize}
              fontWeight='700'
              fontFamily="'Swiss721BT Bold Condensed', sans-serif"
              letterSpacing='-0.05em'
              textLength={textLengths.main || undefined}
              lengthAdjust={textLengths.main ? 'spacingAndGlyphs' : undefined}
              style={{ textTransform: 'uppercase' }}
            >
              {mainText}
            </text>
            <rect
              x={rectX}
              y={rectY}
              width={rectW}
              height={rectH}
              rx={rectRx}
              fill='white'
            />
          </mask>
        </defs>

        {/* Hidden, rendered copies used only to measure natural text width.
            Text inside <defs>/<mask> has no geometry, so getComputedTextLength
            there returns 0 — these visible-but-hidden twins measure reliably. */}
        <text
          ref={topTextRef}
          x={textX}
          y={topTextY}
          textAnchor='middle'
          fontSize={fontSize}
          fontWeight='700'
          fontFamily="'Swiss721BT Bold Condensed', sans-serif"
          letterSpacing='-0.05em'
          visibility='hidden'
          style={{ textTransform: 'uppercase' }}
        >
          {topText}
        </text>
        <text
          ref={mainTextRef}
          x={textX}
          y={mainTextY}
          textAnchor='middle'
          fontSize={fontSize}
          fontWeight='700'
          fontFamily="'Swiss721BT Bold Condensed', sans-serif"
          letterSpacing='-0.05em'
          visibility='hidden'
          style={{ textTransform: 'uppercase' }}
        >
          {mainText}
        </text>
      </svg>

      <div
        className='masked-video-frame__mask'
        style={{
          WebkitMask: `url(#${maskId}) 0 0 / 100% 100%`,
          mask: `url(#${maskId}) 0 0 / 100% 100%`,
        }}
      >
        <div className={`masked-video-frame__video ${videoContainerClassName}`.trim()}>
          {imageSrc ? (
            <OptimizedImage
              avif={imageAvif}
              webp={imageWebp}
              fallback={imageSrc}
              className='masked-video-frame__video-el'
            />
          ) : (
            <video
              ref={videoRef}
              autoPlay
              loop
              muted
              playsInline
              preload='auto'
              className='masked-video-frame__video-el'
            >
              <source src={videoSrc} type='video/mp4' />
            </video>
          )}
        </div>
      </div>

      {children}
    </div>
  );
};

export default MaskedVideoFrame;
